import { utcToLocal12h, getLocalTimezoneAbbr } from './formatters';

const DISPLAY_DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const PICKER_DAY_NAMES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

function shiftDayNumber(dayNumber: number, dayOffset: number): number {
  return ((dayNumber + dayOffset) % 7 + 7) % 7;
}

export type Schedule = {
  minute: number;
  hour: number;
  days: string[];
  ampm: 'AM' | 'PM';
};

export function getScheduleDisplay(cronExpression?: string): string | null {
  if (!cronExpression) return null;

  const parts = cronExpression.split(' ');
  if (parts.length < 5) return null;

  const minutePart = parts[0];
  const hourPart = parts[1];
  const daysPart = parts[4];

  if (!daysPart || !hourPart || !minutePart) return null;

  const dayNumbers = daysPart.split(',').map((d) => parseInt(d, 10)).filter((d) => !isNaN(d));

  if (dayNumbers.length === 0) return null;

  const utcHour = parseInt(hourPart, 10);
  const utcMinute = parseInt(minutePart, 10);

  if (isNaN(utcHour) || isNaN(utcMinute)) return null;

  const { hour12, minute: localMinute, ampm, dayOffset } = utcToLocal12h(utcHour, utcMinute);
  const selectedDays = dayNumbers
    .map((dayNumber) => shiftDayNumber(dayNumber, dayOffset))
    .sort((a, b) => a - b)
    .map((dayNumber) => DISPLAY_DAY_NAMES[dayNumber])
    .filter((dayName): dayName is string => Boolean(dayName));

  const dayLabels = selectedDays.join(', ');
  return `Every ${dayLabels} at ${hour12}:${String(localMinute).padStart(2, '0')} ${ampm} ${getLocalTimezoneAbbr()}`;
}

export function getSchedule(cronExpression?: string): Schedule | null {
  if (!cronExpression) return null;

  const parts = cronExpression.split(' ');
  if (parts.length < 5) return null;

  const minutePart = parts[0];
  const hourPart = parts[1];
  const daysPart = parts[4];

  if (!daysPart || !hourPart || !minutePart) return null;

  const dayNumbers = daysPart.split(',').map((d) => parseInt(d, 10)).filter((d) => !isNaN(d));

  if (dayNumbers.length === 0) return null;

  const hour = parseInt(hourPart, 10);
  const minute = parseInt(minutePart, 10);

  if (isNaN(hour) || isNaN(minute)) return null;

  // Cron stores time in UTC — convert to user's local time for the form picker
  const { hour12, minute: localMinute, ampm, dayOffset } = utcToLocal12h(hour, minute);
  const selectedDays = dayNumbers
    .map((dayNumber) => shiftDayNumber(dayNumber, dayOffset))
    .sort((a, b) => a - b)
    .map((dayNumber) => PICKER_DAY_NAMES[dayNumber])
    .filter((dayName): dayName is string => Boolean(dayName));

  return {
    minute: localMinute,
    hour: hour12,
    days: selectedDays,
    ampm
  };
}

/**
 * Parses the day-of-week field of a 5-part cron expression into a sorted, de-duplicated
 * list of UTC day numbers (0 = Sunday … 6 = Saturday). Supports comma lists (`1,3,5`),
 * ranges (`1-5`), and `*`. Unparseable tokens are ignored.
 */
export function parseCronDays(daysPart: string): number[] {
  const set = new Set<number>();
  for (const rawToken of daysPart.split(',')) {
    const token = rawToken.trim();
    if (token === '') continue;
    if (token === '*') {
      for (let d = 0; d < 7; d++) set.add(d);
      continue;
    }
    const range = token.split('-');
    if (range.length === 2) {
      const start = parseInt(range[0] ?? '', 10);
      const end = parseInt(range[1] ?? '', 10);
      if (!isNaN(start) && !isNaN(end)) {
        for (let d = start; d <= end; d++) set.add(((d % 7) + 7) % 7);
      }
      continue;
    }
    const day = parseInt(token, 10);
    if (!isNaN(day)) set.add(((day % 7) + 7) % 7);
  }
  return [...set].sort((a, b) => a - b);
}

/**
 * Computes the next fire time (as a UTC-backed {@link Date}) for a 5-part cron expression
 * of the form `minute hour * * days`, strictly after `from`. Cron fields are interpreted
 * in UTC (matching how the backend Scheduler evaluates them). Returns `null` for an empty
 * or malformed expression, or if no day is scheduled.
 */
export function getNextRunTime(cronExpression?: string, from: Date = new Date()): Date | null {
  if (!cronExpression) return null;

  const parts = cronExpression.split(' ');
  if (parts.length < 5) return null;

  const minute = parseInt(parts[0] ?? '', 10);
  const hour = parseInt(parts[1] ?? '', 10);
  const daysPart = parts[4];

  if (isNaN(minute) || isNaN(hour) || !daysPart) return null;

  const days = parseCronDays(daysPart);
  if (days.length === 0) return null;

  // Search today plus the next 7 days (a full weekly cycle) for the earliest scheduled
  // occurrence strictly after `from`.
  for (let offset = 0; offset <= 7; offset++) {
    const candidate = new Date(
      Date.UTC(
        from.getUTCFullYear(),
        from.getUTCMonth(),
        from.getUTCDate() + offset,
        hour,
        minute,
        0,
        0
      )
    );
    if (candidate.getTime() <= from.getTime()) continue;
    if (days.includes(candidate.getUTCDay())) return candidate;
  }
  return null;
}

/**
 * Formats a positive duration (milliseconds) as a compact relative string
 * (e.g. `in 2d 3h`, `in 5h 10m`, `in 12m`, `soon`). Non-positive durations return `null`.
 */
export function formatRelativeDuration(ms: number): string | null {
  if (ms <= 0) return null;
  if (ms < 60000) return 'soon';
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `in ${days}d ${hours}h`;
  if (hours > 0) return `in ${hours}h ${minutes}m`;
  return `in ${minutes}m`;
}

/**
 * Produces a human-friendly "next scheduled run" string for a recurring cron expression,
 * combining an absolute local date/time (with timezone abbreviation) and a relative hint.
 * Returns `null` when there is no valid upcoming run.
 */
export function getNextRunDisplay(cronExpression?: string, from: Date = new Date()): string | null {
  const next = getNextRunTime(cronExpression, from);
  if (!next) return null;

  const absolute = next.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
  const relative = formatRelativeDuration(next.getTime() - from.getTime());
  return `${absolute} ${getLocalTimezoneAbbr()}${relative ? ` (${relative})` : ''}`;
}
